// copyQuote.js — "Copy as new quote" (Quote Log detail panel): a saved
// quote's set-up becomes the starting point for a NEW quote. Distinct from
// Amend: saving the copy issues a new number and never touches the source.
//
// Carried over: category, services, contamination, protection, custom labour,
// add-ons, vehicle type, job details (work type, job location), equipment
// and rate settings, margin and contingency, scope
// text with its custom-scope flag, exclusions.
// Cleared: client details, the vehicle itself (registration, make, model,
// year, colour), Industrial travel miles (they belong to the old site; the
// calculator asks for the new site's), scheduled date, estimator
// (defaults back to whoever is signed in), estimator and internal notes, and
// the discount unless copyDiscount. PO details live in their own column, so
// they're never part of the copied state.

const NO_DISCOUNT = { discountType: "none", discountValue: 0, discountTargetPrice: null, discountReason: "" };

// stateJson: the source quote's saved State JSON (parsed). quoteRef: the
// provisional number for the new quote (its prefix tells the Apps Script
// which series to number it in). copyOf: the source quote's number, kept with
// the calculator's state for the "Copy of …" banner.
export function buildCopyState(stateJson, { isAuto, copyDiscount, quoteRef, copyOf }) {
  const s = JSON.parse(JSON.stringify(stateJson || {}));
  // Derived at save time, so not carried; a copy is priced afresh.
  delete s.costBreakdown;
  delete s.totalChargeableHrs;
  delete s._savedTotalExVat;

  const notes = s.notes || {};
  const scope = notes.clientScope || "";
  s.notes = {
    estimator: "",
    internal: "",
    clientScope: scope,
    exclusions: notes.exclusions || "",
    // Older quotes have no flag: any saved scope text counts as custom.
    scopeCustom: notes.scopeCustom !== undefined ? !!notes.scopeCustom : !!scope.trim(),
    // Scope builder fields (scopeTemplates.js), when the source has them;
    // otherwise the calculator keeps the copied scope word for word.
    ...(notes.scopeV === 2 ? {
      scopeV: 2, scopeCustomText: notes.scopeCustomText || "", scopeExtra: notes.scopeExtra || "", scopeLegacy: !!notes.scopeLegacy,
    } : {}),
  };

  const commercial = s.commercial || {};
  if (!copyDiscount) {
    const { _discountSnapshot, ...rest } = commercial;
    s.commercial = { ...rest, ...NO_DISCOUNT };
  }

  if (isAuto) {
    s.job = { ...(s.job || {}), quoteRef, jobDate: "", estimator: "" };
    s.client = { name: "", email: "", phone: "", address: "", postcode: "" };
    s.vehicle = { reg: "", make: "", model: "", year: "", colour: "", vehicleType: s.vehicle?.vehicleType || "Modern / Daily" };
  } else {
    s.job = {
      ...(s.job || {}), quoteRef, jobDate: "", estimator: "",
      clientName: "", clientEmail: "", clientPhone: "", clientPOC: "",
      clientAddress: "", siteName: "", postcode: "",
    };
    s.deploy = { ...(s.deploy || {}), travelMiles: "", manualMiles: null };
  }
  s._copyOf = copyOf || "";
  return s;
}

// Whether the source quote has a discount worth offering to copy.
export function hasDiscount(stateJson) {
  const c = stateJson?.commercial;
  return !!c && c.discountType && c.discountType !== "none" && (Number(c.discountValue) > 0 || c.discountTargetPrice != null || !!c._discountSnapshot);
}
